import Head from 'next/head';
import { GetStaticProps, NextPage } from 'next';
import { groq } from 'next-sanity';
import Image from 'next/image';
import { getClient } from '../../lib/sanity.server';
import { Article } from './articles/[slug]';
import SupportCard from '../../components/learn/SupportCard';
import SupportArticlesButton from '../../components/learn/SupportArticlesButton';
import WhatsNewCard from '../../components/learn/WhatsNewCard';
import AngleButton from '../../components/AngleButton';
import TextForm from '../../components/TextForm';
import OpenResourceCard from '../../components/learn/OpenResourceCard';
import styles from '../../components/learn/learn.module.css';
import ArticlePreviews from '../../components/learn/ArticlePreviews';
import { WhatsNew } from './whats-new';

interface Props {
  singletonLearn: LearnPage;
  articles: Array<Article>;
}

interface LearnPage {
  title: string;
  seo: {
    description: string;
    imageUrl: string;
  };
  featuredSupportArticles?: Support[];
  featuredWhatsNews?: WhatsNew[];
  featuredOpenResources?: Open[];
}

export interface Support {
  title: string;
  description: string;
  url: string;
}

export interface Open {
  title: string;
  imageAsset: string;
  description: string;
  url: string;
}

const Learn: NextPage<Props> = ({ singletonLearn, articles }) => {
  return (
    <>
      <Head>
        <title>{singletonLearn.title}</title>
        <meta name='title' content={singletonLearn.title} />
        <meta
          name='description'
          content={singletonLearn.seo ? singletonLearn.seo.description : ''}
        />
        <meta property='og:image' content={singletonLearn.seo ? singletonLearn.seo.imageUrl : ''} />
      </Head>
      <main>
        <div className={styles['gray-box']} />
        <div className='mx-auto max-w-screen-xl'>
          <div className='mb-24 sm:mb-32 lg:mb-40'>
            <h1
              className={`small ${styles['header-size']} text-center mt-16 sm:mt-24 lg:mt-32 mb-4`}
            >
              Maximize Your Online Ministry
            </h1>
            <div className='sm:grid sm:grid-cols-12'>
              <div className='sm:col-start-2 lg:col-start-3 sm:col-span-10 lg:col-span-8'>
                <p className='mb-10 text-center subhead lg:mb-20'>
                  Find resources and best practices to help you get started or go deeper in online
                  ministry.
                </p>
              </div>
            </div>
            <ArticlePreviews articles={articles} />
          </div>
          <div className='mb-24 sm:mb-32 lg:mb-40'>
            <div className='sm:grid sm:grid-cols-12'>
              <div className='sm:col-start-3 lg:col-start-1 sm:col-span-8 lg:col-span-12'>
                <h3 className='mb-2 section-header sm:mb-4 lg:mb-2'>
                  Using Church Online Platform
                </h3>
                <p className='mb-8 body-paragraph'>
                  Check out our support articles for in-depth guidance to all the Platform features
                </p>
                <div className='lg:flex lg:grid lg:grid-cols-12 lg:gap-8'>
                  {singletonLearn.featuredSupportArticles?.map((featuredSupportArticle, index) => {
                    return (
                      <div
                        key={featuredSupportArticle.title}
                        className='mb-6 lg:col-span-4 sm:mb-8 lg:mb-10'
                      >
                        {index < 3 ? <SupportCard supportArticle={featuredSupportArticle} /> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <SupportArticlesButton />
          </div>
          <div className='mb-24 sm:mb-32 lg:mb-40'>
            <div className='sm:grid sm:grid-cols-12'>
              <div className='sm:col-start-3 sm:col-span-8'>
                <h3 className='mb-6 section-header sm:mb-8'>What&apos;s New</h3>
                {singletonLearn.featuredWhatsNews?.map((featuredWhatsNew, index) => {
                  return (
                    <div key={featuredWhatsNew.title} className='mb-6 sm:mb-8'>
                      {index < 3 ? <WhatsNewCard whatsNew={featuredWhatsNew} /> : null}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className='sm:flex sm:justify-center lg:mt-2'>
              <AngleButton text={'See all updates'} link={'/learn/whats-new'} />
            </div>
          </div>
          <div className='mb-24 sm:mb-32 lg:mb-40'>
            <div className='sm:grid sm:grid-cols-12'>
              <div className='sm:col-start-3 sm:col-span-8 lg:text-center'>
                <h3 className='section-header'>Be in the Know</h3>
                <p className='mb-6 body-paragraph sm:mb-8 lg:mb-10'>
                  Never miss a new training or feature by subscribing to our online ministry emails.
                </p>
                <div className='sm:flex sm:justify-center'>
                  <TextForm formText={'Email'} buttonText={'Sign Up'} />
                </div>
              </div>
            </div>
          </div>
          <div className='mb-24 sm:mb-32 lg:mb-40'>
            <div className='sm:grid sm:grid-cols-12'>
              <div className='sm:col-start-3 sm:col-span-8'>
                <div className='lg:text-center'>
                  <h3 className='mb-6 section-header sm:mb-8'>More Free Resources</h3>
                  <div className='mb-6 sm:mb-8'>
                    <Image
                      src='/images/learn/open_network_by_lifechurch_logo.png'
                      alt='Life.Church Open Network logo'
                      layout='fixed'
                      width={307}
                      height={64}
                    />
                  </div>
                  <p className='mb-6 body-paragraph sm:mb-8'>
                    Church Online Platform is a digital mission of Life.Church, Life.Church also
                    offers Open, which equips pastors and church leaders with free resources and
                    training. Get even more ministry resources on Open.
                  </p>
                </div>
                {singletonLearn.featuredOpenResources?.map((featuredOpenResource, index) => {
                  return (
                    <div key={featuredOpenResource.title} className='mb-6 sm:mb-8'>
                      {index < 3 ? <OpenResourceCard openResource={featuredOpenResource} /> : null}
                    </div>
                  );
                })}
                <div className='lg:flex lg:justify-center'>
                  <AngleButton text={'View more resources'} link={'https://open.life.church'} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export const getStaticProps: GetStaticProps<{
  singletonLearn: LearnPage;
  articles: Array<Article>;
}> = async () => {
  const singletonLearn = await getClient().fetch<LearnPage>(
    groq`
    *[_id == "singletonLearn"][0] {
      title,
      seo {
        description,
        "imageUrl": openGraphImage.asset->url
      },
      featuredSupportArticles[]->{description, title, url},
      featuredWhatsNews[]->{
        "slug": slug.current,
        "createdAt": _createdAt,
        title,
        excerpt
      },
      featuredOpenResources[]-> {
        title,
        "imageAsset": image.asset,
        description,
        url
      }
    }
    `
  );

  const articles = await getClient().fetch<Array<Article>>(
    groq`
    *[_type=="article"] {
      "slug": slug.current,
      "coverVideoUrl": media[0]->.assetId,
      "coverImageAsset": media[0].asset,
      title,
      "category": category->.slug.current
    }
  `
  );

  const articlesWithVideos = await Promise.all(
    articles.map(async article => {
      if (article.coverVideoUrl) {
        const response = await fetch(
          `https://fast.wistia.net/oembed?url=https://home.wistia.com/medias/${article.coverVideoUrl}?embedType=async&videoWidth=640`,
          {
            method: 'GET',
          }
        );

        const data = await response.json();
        article.coverVideoUrl = data.thumbnail_url;
      }
      return article;
    })
  );

  return {
    props: {
      singletonLearn,
      articles: articlesWithVideos,
    },
    revalidate: process.env.APP_ENV === 'production' ? 60000 : 1,
  };
};

export default Learn;
